import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetZoomIcon,
} from '@mui/icons-material';
import { endUserApiClient } from '../utils/api';

type ViewerType = 'image' | 'pdf' | 'office' | 'download';

interface FileViewerProps {
  open: boolean;
  onClose: () => void;
  messageId: string;
  fileName?: string;
}

const OFFICE_VIEWER_BASE = 'https://view.officeapps.live.com/op/embed.aspx';

const getToken = (): string => {
  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) return adminToken;
  const endUserSession = endUserApiClient.getSession();
  if (endUserSession?.token) return endUserSession.token;
  return '';
};

const getViewerTypeFromName = (name: string): ViewerType => {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (/^(png|jpe?g|gif|webp|svg)$/.test(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (/^(docx?|xlsx?|pptx?)$/.test(ext)) return 'office';
  return 'download';
};

export const FileViewer: React.FC<FileViewerProps> = ({
  open,
  onClose,
  messageId,
  fileName,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState<ViewerType>('download');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  const fetchViewerUrl = useCallback(async () => {
    if (!messageId) return;
    setLoading(true);
    setError(null);
    setZoom(1);
    try {
      const token = getToken();
      const res = await fetch(
        `/api/files/${messageId}/viewer?token=${encodeURIComponent(token)}`
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'ファイル情報の取得に失敗しました');
        return;
      }
      setUrl(data.url);
      setViewerType(data.viewerType as ViewerType);
    } catch {
      setError('ファイル情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    if (open) fetchViewerUrl();
    return () => {
      setUrl(null);
      setLoading(true);
      setError(null);
    };
  }, [open, fetchViewerUrl]);

  const handleDownload = () => {
    const token = getToken();
    window.open(
      `/api/files/${messageId}/download?token=${encodeURIComponent(token)}`,
      '_blank'
    );
  };

  const displayName = fileName || 'ファイル';
  const quickType = fileName ? getViewerTypeFromName(fileName) : null;

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">読み込み中...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
          <Typography color="error">{error}</Typography>
          <Button variant="outlined" onClick={handleDownload}>
            ダウンロード
          </Button>
        </Box>
      );
    }

    if (!url) return null;

    switch (viewerType) {
      case 'image':
        return (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              bgcolor: '#1a1a1a',
              cursor: zoom > 1 ? 'grab' : 'default',
            }}
          >
            <img
              src={url}
              alt={displayName}
              style={{
                maxWidth: zoom === 1 ? '100%' : 'none',
                maxHeight: zoom === 1 ? '100%' : 'none',
                width: zoom !== 1 ? `${zoom * 100}%` : undefined,
                objectFit: 'contain',
                transition: 'width 0.2s',
              }}
              draggable={false}
            />
          </Box>
        );

      case 'pdf':
        return (
          <iframe
            src={url}
            title={displayName}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        );

      case 'office':
        return (
          <iframe
            src={`${OFFICE_VIEWER_BASE}?src=${encodeURIComponent(url)}`}
            title={displayName}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        );

      default:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
            <Typography variant="h6" color="text.secondary">
              このファイル形式はプレビューできません
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {displayName}
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
              ダウンロード
            </Button>
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: viewerType === 'image' ? '#1a1a1a' : '#f5f5f5' } }}
    >
      <AppBar position="relative" color="default" elevation={1} sx={{ bgcolor: '#fff' }}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={onClose} aria-label="閉じる">
            <CloseIcon />
          </IconButton>

          <Typography variant="subtitle2" noWrap sx={{ flex: 1, fontWeight: 600 }}>
            {displayName}
          </Typography>

          {(viewerType === 'image' || (quickType === 'image' && loading)) && (
            <>
              <Tooltip title="縮小">
                <IconButton size="small" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} disabled={zoom <= 0.25}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <Tooltip title="拡大">
                <IconButton size="small" onClick={() => setZoom(z => Math.min(5, z + 0.25))}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="リセット">
                <IconButton size="small" onClick={() => setZoom(1)}>
                  <ResetZoomIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title="ダウンロード">
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          {url && viewerType !== 'download' && (
            <Tooltip title="新しいタブで開く">
              <IconButton component="a" href={url} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {renderContent()}
      </Box>
    </Dialog>
  );
};
